import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Form,
    Modal,
    Spinner,
    ListGroup,
    Table,
} from 'react-bootstrap';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { useAuth } from '@/shared/contexts/AuthContext';
import { userService } from '@/shared/services/userService';
import { badgeTextFor } from '@/shared/utils/badgeText';

// Mirrors the role enum on the server's User model. A role stored there and
// missing here renders as its raw value in a badge.
export const ROLE_LABELS = Object.freeze({
    student: 'Student',
    faculty: 'Faculty/staff',
    la_coordinator: 'LA coordinator',
    si_coordinator: 'SI coordinator',
    ta_coordinator: 'TA coordinator',
    admin: 'Admin',
});

// The roles an admin grants here, with what each lets a person do. Student
// is not among them: it is the baseline a person has when they hold none.
const ACCESS_ROLES = [
    {
        role: 'faculty',
        label: 'Faculty/staff: can request peer support for a course',
    },
    { role: 'la_coordinator', label: 'LA coordinator: runs the LA program' },
    { role: 'si_coordinator', label: 'SI coordinator: runs the SI program' },
    { role: 'ta_coordinator', label: 'TA coordinator: runs the TA program' },
    { role: 'admin', label: 'Admin: full access, including this screen' },
];

const USER_TYPE_LABELS = Object.freeze({
    student: 'Student',
    faculty: 'Faculty',
    staff: 'Staff',
});

const PAGE_SIZE = 25;
const PHONE_QUERY = '(max-width: 767.98px)';

// The server refuses this too; saying it on the control is what keeps the
// refusal from being the first place an admin reads the rule.
const SELF_ADMIN_REASON =
    'You cannot remove your own admin role. Ask another admin.';

/**
 * The roles array to save for a set of checked access roles.
 *
 * Student is the baseline: a person holding none of the access roles is a
 * student, and gaining one replaces it, the way profiles created as faculty
 * or coordinators carry no student role. A profile that already held student
 * alongside another role keeps it, so an unrelated edit does not strip it.
 *
 * @param {string[]} originalRoles - The roles the user has now.
 * @param {string[]} checked - The access roles checked in the form.
 * @returns {string[]}
 */
export function resultingRoles(originalRoles, checked) {
    const granted = ACCESS_ROLES.map(({ role }) => role).filter((role) =>
        checked.includes(role)
    );
    if (granted.length === 0) return ['student'];
    const keepsStudent =
        originalRoles.includes('student') &&
        originalRoles.some((role) => role !== 'student');
    return keepsStudent ? ['student', ...granted] : granted;
}

const roleList = (roles) =>
    roles.map((role) => ROLE_LABELS[role] || role).join(', ');

/**
 * What saving would change, in words, or '' when nothing would.
 */
export function describeChange(user, nextRoles, nextUserType) {
    const current = user.roles || [];
    const added = nextRoles.filter((role) => !current.includes(role));
    const removed = current.filter((role) => !nextRoles.includes(role));
    const parts = [];
    if (added.length) parts.push(`gains ${roleList(added)}`);
    if (removed.length) parts.push(`loses ${roleList(removed)}`);
    if (nextUserType !== user.userType) {
        parts.push(
            `account type changes from ${
                USER_TYPE_LABELS[user.userType] || 'not set'
            } to ${USER_TYPE_LABELS[nextUserType]}`
        );
    }
    if (parts.length === 0) return '';
    return `${user.name || user.email} ${parts.join('; ')}.`;
}

const sameRoles = (a, b) =>
    a.length === b.length && a.every((role) => b.includes(role));

function EditAccessModal({ user, show, isSelf, onCancel, onSaved, onExited }) {
    const [checked, setChecked] = useState(() =>
        (user.roles || []).filter((role) => role !== 'student')
    );
    const [userType, setUserType] = useState(user.userType || 'student');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const nextRoles = resultingRoles(user.roles || [], checked);
    const rolesChanged = !sameRoles(nextRoles, user.roles || []);
    const userTypeChanged = userType !== user.userType;
    const summary = describeChange(user, nextRoles, userType);
    const selfAdminLocked = isSelf && (user.roles || []).includes('admin');
    // An account typed Faculty or Staff that holds no access role at all is
    // a student in practice and cannot request support, and re-choosing its
    // current type changes nothing, so the dialog names the mismatch. Staff
    // who hold a coordinator or admin role are not a mismatch: they run a
    // program, and admins can already request support.
    const accessMismatch =
        (userType === 'faculty' || userType === 'staff') &&
        checked.length === 0;
    const who = user.name || user.email;

    // The account type says who someone is; access decides what they can do.
    // Admins read "Account type: Faculty" as granting faculty access, so
    // choosing Faculty or Staff turns that access on and choosing Student
    // turns it off. The checkbox stays editable afterwards for the rare
    // person whose type and access differ.
    const changeUserType = (event) => {
        const next = event.target.value;
        setUserType(next);
        setChecked((previous) => {
            const others = previous.filter((role) => role !== 'faculty');
            return next === 'student' ? others : [...others, 'faculty'];
        });
    };

    const toggle = (role) => (event) =>
        setChecked((previous) =>
            event.target.checked
                ? [...previous, role]
                : previous.filter((r) => r !== role)
        );

    // Roles and account type are separate writes (the roles route and the
    // admin user update), so a failure on the second is reported as such
    // rather than as if nothing had been saved.
    async function handleSave() {
        setSaving(true);
        setError('');
        let updated = user;
        let rolesSaved = false;
        try {
            if (rolesChanged) {
                updated = await userService.updateUserRoles(
                    user._id,
                    nextRoles
                );
                rolesSaved = true;
            }
            if (userTypeChanged) {
                updated = await userService.updateUser(user._id, {
                    userType,
                });
            }
        } catch (err) {
            setSaving(false);
            const reason = err.response?.data?.message;
            if (rolesSaved) {
                onSaved(updated, {
                    variant: 'warning',
                    text: `Access for ${who} was saved, but the account type was not changed. ${
                        reason || 'Try again.'
                    }`,
                });
                return;
            }
            setError(
                reason || 'Could not save. Nothing was changed. Try again.'
            );
            return;
        }
        setSaving(false);
        onSaved(updated, { variant: 'success', text: `Saved. ${summary}` });
    }

    return (
        <Modal
            show={show}
            onHide={saving ? undefined : onCancel}
            onExited={onExited}
            aria-labelledby="edit-access-title"
        >
            <Modal.Header closeButton={!saving}>
                <Modal.Title id="edit-access-title" as="h2" className="h5">
                    Edit access for {who}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <p className="small text-muted mb-3">{user.email}</p>
                <Form.Group as="fieldset" className="mb-3">
                    <Form.Label as="legend" className="fs-6 fw-semibold">
                        Access
                    </Form.Label>
                    {ACCESS_ROLES.map(({ role, label }) => {
                        const locked = role === 'admin' && selfAdminLocked;
                        return (
                            <Form.Check
                                key={role}
                                type="checkbox"
                                id={`edit-access-${role}`}
                                label={label}
                                checked={checked.includes(role)}
                                onChange={toggle(role)}
                                disabled={locked || saving}
                                aria-describedby={
                                    locked
                                        ? 'edit-access-self-admin'
                                        : role === 'faculty' && accessMismatch
                                          ? 'edit-access-mismatch'
                                          : undefined
                                }
                            />
                        );
                    })}
                    {accessMismatch && (
                        <Alert
                            id="edit-access-mismatch"
                            variant="warning"
                            className="py-2 px-3 my-2 small"
                        >
                            The account type is {USER_TYPE_LABELS[userType]},
                            but Faculty/staff access is off, so {who} cannot
                            request peer support. Tick Faculty/staff to grant
                            it.
                        </Alert>
                    )}
                    {selfAdminLocked && (
                        <Form.Text
                            id="edit-access-self-admin"
                            className="d-block text-muted"
                        >
                            {SELF_ADMIN_REASON}
                        </Form.Text>
                    )}
                    <Form.Text className="d-block text-muted mt-2">
                        Staff get the same Faculty/staff access; there is no
                        separate staff role. Set the account type to Staff to
                        record it. With no access checked, the person has
                        student access.
                    </Form.Text>
                </Form.Group>
                <Form.Group controlId="edit-access-user-type" className="mb-3">
                    <Form.Label className="fw-semibold">
                        Account type
                    </Form.Label>
                    <Form.Select
                        value={userType}
                        onChange={changeUserType}
                        disabled={saving}
                        aria-describedby="edit-access-user-type-help"
                    >
                        {Object.entries(USER_TYPE_LABELS).map(
                            ([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            )
                        )}
                    </Form.Select>
                    <Form.Text
                        id="edit-access-user-type-help"
                        className="d-block text-muted"
                    >
                        Choosing Faculty or Staff also turns on Faculty/staff
                        access, and choosing Student turns it off. Adjust the
                        access above if this person's differs.
                    </Form.Text>
                </Form.Group>
                <div
                    className="border rounded p-2 small"
                    aria-live="polite"
                    data-testid="edit-access-summary"
                >
                    {summary ? (
                        <>
                            <span className="fw-semibold">On save: </span>
                            {summary}
                        </>
                    ) : (
                        'No changes yet.'
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline-primary"
                    onClick={onCancel}
                    disabled={saving}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving || !summary}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function EditAccessButton({ user, onEdit, className = '' }) {
    return (
        <Button
            variant="outline-primary"
            size="sm"
            className={`text-nowrap ${className}`.trim()}
            aria-label={`Edit access for ${user.name || user.email}`}
            onClick={() => onEdit(user)}
        >
            Edit access
        </Button>
    );
}

function UsersTab() {
    const { userProfile } = useAuth();
    const [search, setSearch] = useState('');
    const [role, setRole] = useState('');
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState(null);
    const [editing, setEditing] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [page, setPage] = useState(0);
    const phone = useMediaQuery(PHONE_QUERY);

    const load = useCallback(async (params, failure) => {
        setLoading(true);
        setError('');
        try {
            const query = {
                limit: PAGE_SIZE,
                offset: params.page * PAGE_SIZE,
                search: params.search,
            };
            if (params.role) query.role = params.role;
            const data = await userService.getAllUsers(query);
            setUsers(data.users || []);
            setTotal(data.total || 0);
        } catch (_err) {
            setError(failure);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load({ search: '', role: '', page: 0 }, 'Failed to load users.');
    }, [load]);

    const handleSearch = (event) => {
        event.preventDefault();
        setPage(0);
        load({ search, role, page: 0 }, 'Failed to search users.');
    };

    const handleRoleFilter = (event) => {
        setRole(event.target.value);
        setPage(0);
        load(
            { search, role: event.target.value, page: 0 },
            'Failed to search users.'
        );
    };

    const goToPage = (next) => {
        setPage(next);
        load({ search, role, page: next }, 'Failed to load users.');
    };

    const openEditor = (user) => {
        setNotice(null);
        setEditing(user);
        setShowEditor(true);
    };

    const handleSaved = (updated, outcome) => {
        setUsers((previous) =>
            previous.map((user) =>
                user._id === updated._id ? { ...user, ...updated } : user
            )
        );
        setShowEditor(false);
        setNotice(outcome);
    };

    // The modal's own focus restore returns focus to the row's Edit access
    // button; the edited user is kept until then so the modal can fade out
    // with its content.
    const handleExited = () => setEditing(null);

    return (
        <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <h2 className="h5 mb-0">User Management</h2>
                    <span className="text-muted small">
                        {total} total users
                    </span>
                </div>
                <Form onSubmit={handleSearch} className="mt-2">
                    <div className="d-flex flex-wrap gap-2">
                        <Form.Group
                            controlId="admin-users-search"
                            className="flex-grow-1"
                        >
                            <Form.Label visuallyHidden>Search users</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Search by name, email, or student ID..."
                                size="sm"
                                value={search}
                                onChange={(event) =>
                                    setSearch(event.target.value)
                                }
                            />
                        </Form.Group>
                        <Form.Group controlId="admin-users-role">
                            <Form.Label visuallyHidden>
                                Filter by role
                            </Form.Label>
                            <Form.Select
                                size="sm"
                                value={role}
                                onChange={handleRoleFilter}
                            >
                                <option value="">All roles</option>
                                {Object.entries(ROLE_LABELS).map(
                                    ([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </Form.Select>
                        </Form.Group>
                        <Button
                            type="submit"
                            variant="outline-primary"
                            size="sm"
                        >
                            Search
                        </Button>
                    </div>
                </Form>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {notice && (
                    <Alert
                        variant={notice.variant}
                        dismissible
                        onClose={() => setNotice(null)}
                    >
                        {notice.text}
                    </Alert>
                )}
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" role="status">
                            <span className="visually-hidden">
                                Loading users...
                            </span>
                        </Spinner>
                    </div>
                ) : (
                    <>
                        {users.length === 0 && (
                            <p className="text-muted text-center py-3 mb-0">
                                No users match this search.
                            </p>
                        )}
                        {phone ? (
                            <ListGroup
                                as="ul"
                                variant="flush"
                                aria-label="Users"
                            >
                                {users.map((user) => (
                                    <ListGroup.Item
                                        as="li"
                                        key={user._id}
                                        className="px-0"
                                    >
                                        <div className="fw-semibold">
                                            {user.name || '-'}
                                        </div>
                                        <div className="small text-break text-muted">
                                            {user.email}
                                        </div>
                                        <div className="my-2">
                                            {user.roles?.map((role) => (
                                                <Badge
                                                    key={role}
                                                    bg="secondary"
                                                    className="me-1 small"
                                                >
                                                    {ROLE_LABELS[role] || role}
                                                </Badge>
                                            ))}
                                        </div>
                                        <div className="small text-muted mb-2">
                                            {USER_TYPE_LABELS[user.userType] ||
                                                user.userType}
                                            {' · '}
                                            {user.isActive
                                                ? 'Active'
                                                : 'Inactive'}
                                        </div>
                                        <EditAccessButton
                                            user={user}
                                            onEdit={openEditor}
                                            className="w-100 admin-touch-target"
                                        />
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        ) : (
                            <Table hover size="sm" className="align-middle">
                                <thead>
                                    <tr>
                                        <th scope="col">Name</th>
                                        <th scope="col">Email</th>
                                        <th scope="col">Roles</th>
                                        <th scope="col">Type</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => (
                                        <tr key={user._id}>
                                            <td>{user.name || '-'}</td>
                                            <td className="small text-break">
                                                {user.email}
                                            </td>
                                            <td>
                                                {user.roles?.map((role) => (
                                                    <Badge
                                                        key={role}
                                                        bg="secondary"
                                                        className="me-1 small"
                                                    >
                                                        {ROLE_LABELS[role] ||
                                                            role}
                                                    </Badge>
                                                ))}
                                            </td>
                                            <td className="small text-nowrap">
                                                {USER_TYPE_LABELS[
                                                    user.userType
                                                ] || user.userType}
                                            </td>
                                            <td>
                                                <Badge
                                                    bg={
                                                        user.isActive
                                                            ? 'success'
                                                            : 'danger'
                                                    }
                                                    text={badgeTextFor(
                                                        user.isActive
                                                            ? 'success'
                                                            : 'danger'
                                                    )}
                                                >
                                                    {user.isActive
                                                        ? 'Active'
                                                        : 'Inactive'}
                                                </Badge>
                                            </td>
                                            <td className="text-nowrap">
                                                <EditAccessButton
                                                    user={user}
                                                    onEdit={openEditor}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </>
                )}
                {!loading && total > PAGE_SIZE && (
                    <nav
                        aria-label="Users pages"
                        className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3"
                    >
                        <span className="text-muted small">
                            Showing {page * PAGE_SIZE + 1} to{' '}
                            {page * PAGE_SIZE + users.length} of {total} users
                        </span>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={page === 0}
                                onClick={() => goToPage(page - 1)}
                            >
                                Previous
                            </Button>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={(page + 1) * PAGE_SIZE >= total}
                                onClick={() => goToPage(page + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    </nav>
                )}
            </Card.Body>
            {editing && (
                <EditAccessModal
                    key={editing._id}
                    user={editing}
                    show={showEditor}
                    isSelf={Boolean(
                        userProfile?._id && editing._id === userProfile._id
                    )}
                    onCancel={() => setShowEditor(false)}
                    onSaved={handleSaved}
                    onExited={handleExited}
                />
            )}
        </Card>
    );
}

export default UsersTab;
